import { SizableText, YStack } from '@mythical/ui'
import { RenderBlocks } from 'app/components/blocks/RenderBlocks'
import { Meta } from 'app/components/primitive/Meta'
import QuarterlyCollectibles from 'app/components/primitive/QuarterlyCollectibles'
import { Container } from 'app/components/structure/Container'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import useMerchPage from 'app/hooks/use-merch-page'
import useQuery from 'app/hooks/use-query'
import {
  buildRoadmapCollectiblesVariables,
  buildPurchasableCollectiblesVariables,
  createCollectiblesQuery,
  mapToCollectibles,
  sortCollectiblesByQuarterOrder,
} from 'app/queries/collectibles/list'
import { observer } from 'mobx-react-lite'

const query = createCollectiblesQuery()

export const MerchScreen = observer(() => {
  const {
    pageContent,
    collectiblesRoadmapId,
    collectiblesYear,
    roadmapQuarterCollectibleIds,
  } = useMerchPage()
  const { data: yearFilteredCollectibles = [] } = useQuery(
    query,
    buildRoadmapCollectiblesVariables(collectiblesRoadmapId, collectiblesYear),
    mapToCollectibles,
  )
  const { data: purchasableCollectibles = [] } = useQuery(
    query,
    buildPurchasableCollectiblesVariables(),
    mapToCollectibles,
  )
  const pageContentBlocks = pageContent?.data?.pageContent || []
  const purchasableCollectiblesTitle =
    pageContent?.data?.purchasableCollectiblesTitle ||
    'Purchasable Collectibles'
  const purchasableCollectiblesDescription =
    pageContent?.data?.purchasableCollectiblesDescription ||
    'Eligible members can purchase these collectible drops directly from the store.'
  const orderedYearCollectibles = sortCollectiblesByQuarterOrder(yearFilteredCollectibles)
  const roadmapTopCarouselItems = roadmapQuarterCollectibleIds
    .map((id) => orderedYearCollectibles.find((item) => item.id === id))
    .filter(
      (item): item is (typeof orderedYearCollectibles)[number] => Boolean(item),
    )
  const topCarouselItems =
    roadmapTopCarouselItems.length > 0
      ? roadmapTopCarouselItems
      : orderedYearCollectibles.slice(0, 4)

  return (
    <MainLayout
      showBackBtn
      contentStyles={{
        gap: '$7',
        $gtMd: {
          gap: '$8',
        },
      }}
    >
      <Meta title="Merch & Collectibles" slug="merch" />

      <YStack gap="$5">
        <Container paddedX variant="none">
          <SizableText ff="$heading" tag="h1" size="$l">
            Merch & Collectibles
          </SizableText>
        </Container>

        <QuarterlyCollectibles items={topCarouselItems} />
      </YStack>

      {pageContentBlocks.length > 0 && (
        <RenderBlocks blocks={pageContentBlocks} slug="merch" spaced />
      )}

      {purchasableCollectibles.length > 0 && (
        <YStack gap="$4">
          <Container paddedX variant="none" gap="$1.5">
            <SizableText ff="$heading" tag="h2" size="$m">
              {purchasableCollectiblesTitle}
            </SizableText>
            <SizableText size="$m" color="$fgSecondary">
              {purchasableCollectiblesDescription}
            </SizableText>
          </Container>

          <QuarterlyCollectibles
            items={purchasableCollectibles}
            showUpsellPrompt={false}
          />
        </YStack>
      )}
    </MainLayout>
  )
})
